import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EffectRef,
  InputSignal,
  OnDestroy,
  OutputRef,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  parseXML,
  removeChildren,
  upsertChildren,
  UserInformation,
  xmlElementToString
} from '@lfw/roesberg.liveforms.core';
import {
  BASE_TEXT_COLOR_VARIANTS,
  extractColorProperties,
  getColors,
  getHexOrRgbaToArgb,
  TextColorVariant
} from '../../../../utils/colors';
import { TableRange } from '../../items/TableItem/table-item-form.types';
import { EditorSettings } from '../../types/editor-settings';
import { BaseItemComponent } from '../BaseItem/base-item-form.component';
import {
  InteractiveCaptionLeftProperties,
  InteractiveColorProperties,
  InteractiveItemProperties
} from './interactive-item-properties';

// Explicit interfaces for better IDE support
export interface IHasInputColorControls {
  hasInputColorControl: boolean;
  currentInputBgColor: WritableSignal<string>;
  currentInputTextColor: WritableSignal<TextColorVariant>;
  inputColorEffect: EffectRef;
  handleInputColorStorage: (element: Element) => void;
  initInputColorProperties: (props: Record<string, any>) => string;
}

export interface IHasRangeColorControls {
  hasRangeColorControl: boolean;
  setRangeBackgroundColor: (color: string, range?: TableRange) => void;
}

export interface IHasCaptionLeft {
  hasCommonCaptionLeftWidth: boolean;
  updateFirstColumnWidthRequired: OutputRef<{ id: string; width: number }>;
  firstColumnWidth: Signal<number>;
  firstSiblingItemId: Signal<string>;
  sharedFirstColumnWidths: Signal<Record<string, number>>;
  gridStyle: Signal<Record<string, string>>;
  getSecondColumnValue: () => string;
}

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export abstract class InteractiveItemComponent<T>
  extends BaseItemComponent<InteractiveItemProperties & T>
  implements OnDestroy
{
  formControlId = '';

  ngOnDestroy(): void {
    this.form().removeControl(this.formControlId);
  }

  override getXML(element?: Element): string {
    const newXmlElement = element || this.getXmlCloneElement();
    return xmlElementToString(this.setResultChildren(newXmlElement)) || '';
  }

  getXmlCloneElement(): Element {
    const newXmlString = xmlElementToString(this.xml());
    return parseXML(newXmlString || '').documentElement;
  }

  setResultChildren(element: Element): Element {
    return upsertChildren(element, {
      ResultValue: this.properties.resultValue,
      ResultUser: this.properties.resultUser,
      ResultTime: this.properties.resultTime
    });
  }
}

type GenericConstructor<T = object> = abstract new (...args: any[]) => T;

export function iHasInputColorControls<
  T extends InteractiveColorProperties,
  TBase extends GenericConstructor<InteractiveItemComponent<T>>
>(Base: TBase) {
  abstract class HasInputColorControls extends (Base as any) {
    hasInputColorControl = true;

    currentInputBgColor = signal('');
    currentInputTextColor = signal<TextColorVariant>(BASE_TEXT_COLOR_VARIANTS.black);

    inputColorEffect = effect(() => {
      (this as unknown as InteractiveItemComponent<any>).properties.inputColor = getHexOrRgbaToArgb(
        this.currentInputBgColor()
      );
    });

    handleInputColorStorage = (element: Element): void => {
      removeChildren(element, ['InputColor']);
      const { properties } = this as unknown as InteractiveItemComponent<any>;
      if (properties.inputColor) {
        upsertChildren(element, {
          InputColor: properties.inputColor
        });
      }
    };

    initInputColorProperties = (props: Record<string, any>): string => {
      const { inputTextColor, inputBgColor } = getColors(extractColorProperties(props));
      this.currentInputBgColor.set(inputBgColor);
      this.currentInputTextColor.set(inputTextColor);

      return inputBgColor;
    };
  }

  return HasInputColorControls as unknown as TBase &
    (abstract new (...args: any[]) => {
      hasInputColorControl: boolean;
      currentInputBgColor: WritableSignal<string>;
      currentInputTextColor: WritableSignal<TextColorVariant>;
      inputColorEffect: EffectRef;
      handleInputColorStorage: (element: Element) => void;
      initInputColorProperties: (props: Record<string, any>) => string;

      // Expose interactive class properties
      formControlId: string;
      getXmlCloneElement: () => Element;
      setResultChildren: (element: Element) => Element;
      properties: InteractiveItemProperties & T;

      // Expose base class properties through the mixin chain
      form: InputSignal<FormGroup>;
      user: InputSignal<UserInformation>;
      xml: InputSignal<ChildNode>;
      settings: InputSignal<EditorSettings>;
      isVisible: WritableSignal<boolean>;
      isGenericRepeatItem: WritableSignal<boolean>;
    });
}

export function iHasRangeColorControls<
  T extends InteractiveColorProperties,
  TBase extends GenericConstructor<InteractiveItemComponent<T>>
>(Base: TBase) {
  abstract class HasRangeColorControls extends (Base as any) {
    hasRangeColorControl = true;
  }

  return HasRangeColorControls as unknown as TBase &
    (abstract new (...args: any[]) => {
      hasRangeColorControl: boolean;
      setRangeBackgroundColor: (color: string, range?: TableRange) => void;

      // Expose interactive class properties
      formControlId: string;
      getXmlCloneElement: () => Element;
      setResultChildren: (element: Element) => Element;
      properties: InteractiveItemProperties & T;

      // Expose base class properties
      form: InputSignal<FormGroup>;
      user: InputSignal<UserInformation>;
      xml: InputSignal<ChildNode>;
      settings: InputSignal<EditorSettings>;
      isVisible: WritableSignal<boolean>;
      isGenericRepeatItem: WritableSignal<boolean>;
    });
}

export function iHasCaptionLeft<
  T extends InteractiveCaptionLeftProperties,
  TBase extends GenericConstructor<InteractiveItemComponent<T>>
>(Base: TBase) {
  abstract class HasCommonCaptionLeftWidth extends (Base as any) {
    public hasCommonCaptionLeftWidth = true;
    declare updateFirstColumnWidthRequired: OutputRef<{ id: string; width: number }>;
    declare firstColumnWidth: Signal<number>;
    declare firstSiblingItemId: Signal<string>;
    declare sharedFirstColumnWidths: Signal<Record<string, number>>;
  }

  return HasCommonCaptionLeftWidth as unknown as TBase &
    (abstract new (...args: any[]) => {
      hasCommonCaptionLeftWidth: boolean;
      sharedFirstColumnWidths: WritableSignal<Record<string, number>>;
      gridStyle: Signal<Record<string, string>>;
      getSecondColumnValue: () => string;

      // Expose interactive class properties
      formControlId: string;
      getXmlCloneElement: () => Element;
      setResultChildren: (element: Element) => Element;
      properties: InteractiveItemProperties & T;

      // Expose base class properties
      form: InputSignal<FormGroup>;
      user: InputSignal<UserInformation>;
      xml: InputSignal<ChildNode>;
      settings: InputSignal<EditorSettings>;
      isVisible: WritableSignal<boolean>;
      isGenericRepeatItem: WritableSignal<boolean>;
    });
}
